import React from 'react';
import { DashboardStats } from '../../../../services/profileApi';

interface BusinessDashboardProps {
  stats: DashboardStats | null;
  expandedSections: {[key: string]: boolean};
  setExpandedSections: (sections: {[key: string]: boolean}) => void;
  currentRevenue: number;
  revenueChangePercent?: number;
}

const BusinessDashboard: React.FC<BusinessDashboardProps> = ({
  stats,
  expandedSections,
  setExpandedSections,
  currentRevenue,
  revenueChangePercent
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  return (
    <>
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          <div className="flex flex-col rounded-lg p-4 transition-colors duration-150 hover:bg-black/2">
            <span className="text-xs text-black font-bold mb-2 uppercase tracking-wide">Doanh thu (tháng)</span>
            <div className="text-3xl font-light text-black mb-1">{formatCurrency(currentRevenue).split('₫')[0]}₫</div>
            {revenueChangePercent !== undefined && (
              <span className={`text-xs ${revenueChangePercent > 0 ? 'text-black' : 'text-black/40'}`}>
                {revenueChangePercent > 0 ? '+' : ''}{revenueChangePercent.toFixed(1)}%
              </span>
            )}
          </div>

          <div className="flex flex-col rounded-lg p-4 transition-colors duration-150 hover:bg-black/2">
            <span className="text-xs text-black font-bold mb-2 uppercase tracking-wide">Khách thuê</span>
            <div className="text-3xl font-light text-black mb-1">{stats?.profile_specific_stats?.total_tenants || 0}</div>
            <span className="text-xs text-black/40">Tổng số</span>
          </div>

          <div className="flex flex-col rounded-lg p-4 transition-colors duration-150 hover:bg-black/2">
            <span className="text-xs text-black font-bold mb-2 uppercase tracking-wide">Tỷ lệ lấp đầy</span>
            <div className="text-3xl font-light text-black mb-1">
              {(stats?.profile_specific_stats?.occupancy_rate || 0).toFixed(0)}%
            </div>
            <span className="text-xs text-black/40">Bất động sản</span>
          </div>

          <div className="flex flex-col rounded-lg p-4 transition-colors duration-150 hover:bg-black/2">
            <span className="text-xs text-black font-bold mb-2 uppercase tracking-wide">Thời gian thuê TB</span>
            <div className="text-3xl font-light text-black mb-1">
              {(stats?.profile_specific_stats?.average_rental_duration_days || 0).toFixed(0)}
            </div>
            <span className="text-xs text-black/40">Ngày</span>
          </div>
        </div>
      )}

      {stats?.profile_specific_stats && (
        <div className="mb-12">
          <div 
            className="mb-8 cursor-pointer" 
            onClick={() => setExpandedSections({...expandedSections, profileSpecific: !expandedSections.profileSpecific})}
          >
            <div className="flex items-center gap-3 mb-2">
              <button className="w-6 h-6 flex items-center justify-center hover:bg-black/5 rounded transition-colors">
                <i className={`fas fa-chevron-${expandedSections.profileSpecific ? 'down' : 'right'} text-xs text-black/60`}></i>
              </button>
              <div className="w-1 h-6 bg-black"></div>
              <h3 className="text-sm text-black font-bold uppercase tracking-wide">
                Chỉ số doanh nghiệp
              </h3>
            </div>
            <p className="text-xs text-black/40 ml-10">Các số liệu chuyên sâu cho tài khoản của bạn</p>
          </div>

          {expandedSections.profileSpecific && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="group rounded-lg border border-black/5 p-4 transition-all duration-150 hover:border-black/10 hover:bg-black/2">
                <span className="text-xs text-black/50 font-bold mb-2 uppercase tracking-wide block">Khách thuê</span>
                <div className="text-2xl font-light text-black mb-1">{stats.profile_specific_stats.total_tenants || 0}</div>
                <span className="text-xs text-black/30">Tổng số</span>
              </div>

              <div className="group rounded-lg border border-black/5 p-4 transition-all duration-150 hover:border-black/10 hover:bg-black/2">
                <span className="text-xs text-black/50 font-bold mb-2 uppercase tracking-wide block">Tỷ lệ lấp đầy</span>
                <div className="text-2xl font-light text-black mb-1">
                  {(stats.profile_specific_stats.occupancy_rate || 0).toFixed(0)}%
                </div>
                <span className="text-xs text-black/30">Bất động sản</span>
              </div>

              <div className="group rounded-lg border border-black/5 p-4 transition-all duration-150 hover:border-black/10 hover:bg-black/2">
                <span className="text-xs text-black/50 font-bold mb-2 uppercase tracking-wide block">Tiền cọc</span>
                <div className="text-2xl font-light text-black mb-1">
                  {formatCurrency(stats.profile_specific_stats.total_deposit || 0).split('₫')[0]}₫
                </div>
                <span className="text-xs text-black/30">Tổng cọc</span>
              </div>

              <div className="group rounded-lg border border-black/5 p-4 transition-all duration-150 hover:border-black/10 hover:bg-black/2">
                <span className="text-xs text-black/50 font-bold mb-2 uppercase tracking-wide block">Thời gian thuê TB</span>
                <div className="text-2xl font-light text-black mb-1">
                  {(stats.profile_specific_stats.average_rental_duration_days || 0).toFixed(0)}
                </div>
                <span className="text-xs text-black/30">Ngày</span>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default BusinessDashboard;
